<template>
  <div id="page-product-form">
    <vs-row>
      <vs-col :class="readonly ? 'readonly-form' : ''">
        <vue-element-loading :active="busy"></vue-element-loading>
        <card-page card-title="Details" :switch-model="model.IsEnabled">
          <vue-form-generator
              ref="form"
              :model="model"
              :options="formOptions"
              :schema="schema"
              v-if="!loading"
          />
        </card-page>
        <card-page card-title="Sections" v-if="!this.readonly">
          <additive-widget
              ref="formSections"
              v-model="model"
              :form-options="formOptions"
              :schema="sectionsSchema"
          />
        </card-page>
        <card-page card-title="Insurers">
          <additive-widget
              class="underwriters-form"
              ref="formUnderwriters"
              v-model="model"
              :form-options="formOptions"
              :schema="underwritersSchema"
          />
        </card-page>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>

import FormModelMixin from '@/components/mixins/FormModelMixin';
import FormFieldValidationMixin
  from '@/components/mixins/FormFieldValidationMixin';
import {
  arrayOptions,
  datepickerOptions,
  selectOptions
} from '@/components/form/FormElementOption';
import moment from 'moment';
import CardPage from '../../../components/Views/CardPage';
import AdditiveWidget from '../../../../components/form/fields/AdditiveWidget';

export default {
  name: 'FDOForm',
  props: {
    value: {
      type: Object,
      required: true
    },
    cardTitle: {
      type: String,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    facilityId: {
      required: true,
    }
  },
  components: {
    CardPage,
    AdditiveWidget,
  },
  mixins: [FormModelMixin, FormFieldValidationMixin],
  data() {
    return {
      additiveForms: [
        'formSections',
        'formUnderwriters',
      ],
      model: {
        Market_FK: {},
        FDODescription: null,
        TotalPercentage: null,
        InceptionDate: null,
        ExpiryDate: null,
        IsEnabled: 0,
        sections: [],
        underwriters: [],
      },
      sectionsSchema: {
        groups:[
          {
            fields: [
              {
                model: 'sections',
                ...arrayOptions,
                hideAddButton: this.readonly,
                showRemoveButton: !this.readonly,
                items: {
                  type: 'object',
                  styleClasses: 'w-full',
                  schema: {
                    groups: [
                      {
                        styleClasses: 'w-full',
                        fields: [
                          {
                            disabled: () => this.readonly,
                            type: 'vueMultiSelect',
                            inputType: 'text',
                            styleClasses: 'w-1/3',
                            label: 'Section',
                            model: 'Section_FK',
                            // Only show ID's not selected
                            values: () => this.sections.filter(({id}) =>
                                !this.sectionIdsTaken.includes(id)),
                            selectOptions,
                            validator: 'required',
                            required: !this.readonly,
                          },
                        ]
                      }
                    ]
                  }
                }
              },
            ]
          },
        ]
      },
      underwritersSchema: {
        groups:[
          {
            fields: [
              {
                model: 'underwriters',
                ...arrayOptions,
                hideAddButton: this.readonly,
                showRemoveButton: !this.readonly,
                items: {
                  type: 'object',
                  default: {
                    CanDoEndorsement: 0,
                    CanDoReferral: 0,
                    SlipLeaderFee: 0,
                    InsurerShare: 0,
                    OrderShare_PC: 0,
                  },
                  schema: {
                    groups: [
                      {
                        styleClasses: 'w-full additive-separator-label-wrapper',
                        fields: [
                          {
                            visible: this.readonly,
                            type: 'label',
                            styleClasses: 'w-full additive-separator-label',
                            get: model => model.CompanyInsurer_FK.name
                          },
                        ]
                      },
                      {
                        styleClasses: 'w-full',
                        fields: [
                          {
                            disabled: () => this.readonly,
                            type: 'vueMultiSelect',
                            styleClasses: 'w-1/3',
                            label: 'Insurer',
                            model: 'CompanyInsurer_FK',
                            values: () => this.companyInsurers.filter(({id}) =>
                                !this.companyIdsTaken.includes(id)),
                            selectOptions,
                            validator: 'required',
                            required: !this.readonly,
                          },
                          {
                            disabled: () => this.readonly,
                            type: 'vueMultiSelect',
                            styleClasses: 'w-1/3',
                            label: 'Insurer Role',
                            model: 'InsurerRole_FK',
                            values: () => this.insurerRoles,
                            selectOptions,
                            validator: 'required',
                            required: !this.readonly,
                          },
                          {
                            disabled: () => this.readonly,
                            type: 'input',
                            inputType: 'number',
                            styleClasses: 'w-1/3',
                            label: 'Slip Leader Fee %',
                            model: 'SlipLeaderFee',
                            required: !this.readonly,
                            validator: ['required', 'positive', 'numeric'],
                          },
                        ]
                      },
                      {
                        styleClasses: 'w-full mt-2',
                        fields: [
                          {
                            disabled: () => this.readonly,
                            type: 'input',
                            inputType: 'number',
                            styleClasses: 'w-1/3',
                            label: 'Share % of whole portfolio',
                            model: 'InsurerShare',
                            required: !this.readonly,
                            validator: ['required', 'positive', 'numeric'],
                          },
                          {
                            disabled: () => this.readonly,
                            type: 'input',
                            inputType: 'number',
                            styleClasses: 'w-1/3',
                            label: 'Share % of order',
                            model: 'OrderShare_PC',
                            validator: ['positive', 'numeric'],
                          },
                          {
                            disabled: () => this.readonly,
                            type: 'vsCheckbox',
                            styleClasses: 'w-1/3',
                            label: 'Can refer',
                            model: 'CanDoReferral',
                            default: 0,
                            required: !this.readonly,
                            onChanged: (model) => model.CanDoReferral = Number(model.CanDoReferral),
                          },
                          {
                            disabled: () => this.readonly,
                            type: 'vsCheckbox',
                            styleClasses: 'w-1/3',
                            label: 'Can endorse',
                            model: 'CanDoEndorsement',
                            default: 0,
                            required: !this.readonly,
                            onChanged: (model) => model.CanDoEndorsement = Number(model.CanDoEndorsement),
                          },
                        ]
                      },
                      {
                        styleClasses: 'w-2/3 mt-2',
                        fields: [
                          {
                            model: 'sections',
                            ...arrayOptions,
                            hideAddButton: true,
                            showRemoveButton: false,
                            items: {
                              type: 'object',
                              schema: {
                                groups: [
                                  {
                                    styleClasses: 'w-full',
                                    fields: [
                                      {
                                        disabled: true,
                                        type: 'input',
                                        inputType: 'hidden',
                                        styleClasses: 'hidden',
                                        label: 'Section',
                                        model: 'Section_FK',
                                      },
                                      {
                                        disabled: true,
                                        type: 'label',
                                        styleClasses: 'w-1/2',
                                        label: 'Section',
                                        model: 'Section',
                                      },
                                      {
                                        disabled: () => this.readonly,
                                        type: 'input',
                                        inputType: 'text',
                                        styleClasses: 'w-1/2',
                                        label: 'Reference',
                                        model: 'InsurerSectionReference',
                                        required: !this.readonly,
                                        validator: 'string',
                                      },
                                    ]
                                  },
                                ]
                              }
                            }
                          }
                        ]
                      },
                    ]
                  }
                }
              },
            ]
          },
        ]
      },
      schema: {
        groups: [
          {
            styleClasses: 'w-full',
            legend: 'General',
            fields: [
              {
                type: 'input',
                inputType: 'text',
                disabled: () => this.readonly,
                styleClasses: 'w-1/3',
                label: 'Name',
                model: 'FDOName',
                required: !this.readonly,
                validator: ['required', 'string', async (value, field, model) => {
                  if (value.trim() === '') {
                    return 'Only white spaces detected';
                  }
                }],
              },
              {
                type: 'input',
                inputType: 'text',
                disabled: () => this.readonly,
                styleClasses: 'w-1/3',
                label: 'Description',
                model: 'FDODescription',
                required: !this.readonly,
                validator: ['required', 'string', async (value, field, model) => {
                  if (value.trim() === '') {
                    return 'Only white spaces detected';
                  }
                }],
              },
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                styleClasses: 'w-1/3',
                label: 'Market',
                model: 'Market_FK',
                values: () => this.markets,
                selectOptions,
                validator: 'required',
                required: !this.readonly,
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'number',
                step: '.1',
                min: 0,
                max: 100,
                styleClasses: 'w-1/3',
                label: 'Total %',
                model: 'TotalPercentage',
                required: !this.readonly,
                validator: ['required', 'positive', 'numeric'],
              },
            ]
          },
          {
            styleClasses: 'w-full',
            legend: 'Dates',
            fields: [
              {
                ...datepickerOptions,
                disabled: () => this.readonly,
                styleClasses: 'w-1/3',
                label: 'Inception Date',
                model: 'InceptionDate',
                required: !this.readonly,
                onChanged: () => {
                  this.validateField('ExpiryDate');
                },
                validator: ['required', (value, field, model) => {
                  if (model.ExpiryDate) {
                    let expiryDate = model.ExpiryDate;
                    let inceptionDate = moment(model.InceptionDate).unix();

                    if (typeof expiryDate === 'string') {
                      expiryDate = moment(model.ExpiryDate, "YYYY-MM-DD").unix();
                    } else {
                      expiryDate = expiryDate / 1000;
                    }

                    if (inceptionDate > expiryDate) {
                      return 'Inception Date must be before Expiry Date.';
                    }
                  }

                  return true;
                }],
              },
              {
                disabled: () => this.readonly,
                ...datepickerOptions,
                styleClasses: 'w-1/3',
                label: 'Expiry Date',
                model: 'ExpiryDate',
                required: !this.readonly,
                onChanged: () => {
                  this.validateField('InceptionDate');
                },
                validator: ['string', (value, field, model) => {
                  if (model.InceptionDate) {
                    let inceptionDate = model.InceptionDate;
                    let expiryDate = moment(model.ExpiryDate).unix();

                    if (typeof inceptionDate === 'string') {
                      inceptionDate = moment(model.InceptionDate, "YYYY-MM-DD").unix();
                    } else {
                      inceptionDate = inceptionDate / 1000;
                    }

                    if (expiryDate < inceptionDate) {
                      return 'Expiry Date must be after Inception Date.';
                    }
                  }

                  return true;
                }],
              },
            ]
          },
        ],
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true,
      },
      companyInsurers: [],
      sections: [],
      markets: [],
      insurerRoles: [],
      options: [
        {
          endpoint: () => this.$api.companies.insurers(),
          optionsFieldName: 'companyInsurers',
          nameFieldName: 'CompanyName',
          idFieldName: 'CompanyInsurer_FK',
          modelArrayName: 'underwriters'
        },
        {
          endpoint: () => this.$api.facilityInsurerRole.list(),
          optionsFieldName: 'insurerRoles',
          nameFieldName: 'InsurerRole',
          idFieldName: 'InsurerRole_FK',
          modelArrayName: 'underwriters'
        },
        {
          endpoint: () => this.$api.facilities.markets.list(this.facilityId),
          optionsFieldName: 'markets',
          nameFieldName: 'MarketDescription',
          idFieldName: 'Market_FK',
        },
        {
          endpoint: () => this.$api.facilities.sections.list(this.facilityId),
          optionsFieldName: 'sections',
          nameFieldName: 'Section',
          idFieldName: 'Section_FK',
          modelArrayName: 'sections',
        },
      ]
    };
  },
  watch: {
    sectionsTaken: {
      handler(newSections) {
        if (newSections.length) {
          this.syncSections(newSections);
        }
      },
    },
    'model.underwriters': {
      handler(newUnderwriters, oldUnderwriters) {
        newUnderwriters
            .forEach((underwriter, index) => {
              if (typeof underwriter === 'undefined') {
                return true;
              }


              if (underwriter.sections && underwriter.sections.length > 0) {
                return true;
              }

              this.$set(this.model.underwriters[index], 'sections', this.makeSections(this.sectionsTaken));
            });
      }
    }
  },
  computed: {
    isEdit() {
      return typeof this.value.id !== 'undefined';
    },
    sectionsTaken() {
      return this.model.sections
          .filter((section) => typeof section !== 'undefined' && section.Section_FK);
    },
    sectionIdsTaken() {
      return this.sectionsTaken
          .map(({Section_FK}) => Section_FK.id);
    },
    companiesTaken() {
      return this.model.underwriters
          .filter((underwriter) => typeof underwriter !== 'undefined' && underwriter.CompanyInsurer_FK);
    },
    companyIdsTaken() {
      return this.companiesTaken.map(({CompanyInsurer_FK}) => CompanyInsurer_FK.id);
    },
  },
  methods: {
    /**
     * Sync sections on underwriters, this happens whenever a section is added/removed
     **/
    syncSections(newSections) {
      const sectionIds = newSections.map(({Section_FK}) => Section_FK.id);

      const newUnderwriters = this.model.underwriters.map((row, i) => {
        if (!row.sections) {
          row.sections = [];
        }
        const sections = row.sections;

        // Remove sections
        sections.forEach((section, index) => {
          // We keep these
          if (sectionIds.includes(section.Section_FK)) {
            return true;
          }

          // Remove
          sections.splice(index, 1);
        });

        // Get our IDs that we already have
        const existingSectionIds = sections.map(({Section_FK}) => Section_FK);

        newSections.filter(({Section_FK}) => !existingSectionIds.includes(Section_FK.id))
            .forEach(({Section_FK}) => {
              const {id, name} = Section_FK;
              sections.push({
                Section_FK: id,
                Section: name,
                InsurerSectionReference: null,
              });
            });

        // Sort sections by their Section_FK if sections is not empty or null
        if (sections && sections.length > 0) {
          sections.sort((a, b) => a.Section_FK - b.Section_FK);
        }

        return {
          ...row,
          sections
        };
      });

      this.$set(this.model, 'underwriters', newUnderwriters);
    },
    /**
     *
     * @param sections
     * @returns {*}
     */
    makeSections(sections) {
      return sections.map(({Section_FK}) => {
        const {id, name} = Section_FK;
        return {
          Section_FK: id,
          Section: name,
          InsurerSectionReference: null,
        };
      });
    },
  },
};
</script>

<style lang="scss">
.vue-form-generator .form-group label > span:empty {
  display: none;
}

.additive-separator-label-wrapper {
  display: none;
}

.readonly-form {
  .additive-widget.underwriters-form {
    > div.form-wrapper {
      margin: 0 !important;

      #underwriters.form-array-editor > div > span {
        margin-top: -1px;
        padding: 0 !important;

        .vue-form-generator {
          .additive-separator-label-wrapper {
            display: block;
            margin-left: -1rem;
            margin-right: -1rem;
            width: calc(100% + 2rem) !important; // the 2rem is the sum of both the negative margins as natural numbers
          }

          .form-group.additive-separator-label {
            background-color: #F8F8F8;
            padding: 1rem !important;
            margin-bottom: 1.5rem;
            border: 1px solid #d5dae075;
            font-size: 16px;
            color: #000;
          }
        }
      }
    }
  }
}
</style>
